import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  useGetMachine,
  useUpdateMachine,
  getGetMachineQueryKey,
} from '@/lib/api/orval/target/machines/machines';
import {
  uploadMachineImage,
  deleteMachineImage,
  designateMainImage,
} from '@/lib/api/client';
import { useAuthStore } from '@/stores/authStore';
import { errorMessage } from '@/lib/errors';

export function MachineEdit(): React.ReactElement {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const fileInput = useRef<HTMLInputElement>(null);

  const { data: machine, isLoading, error } = useGetMachine(publicId, {
    query: { enabled: Boolean(publicId) },
  });

  const [form, setForm] = useState({
    name: '',
    short_description: '',
    tags: '',
    is_published: false,
    secret_share_code: '',
    readme_text: '',
  });

  useEffect(() => {
    if (machine) {
      setForm({
        name: machine.name ?? '',
        short_description: machine.short_description ?? '',
        tags: (machine.tags ?? []).join(', '),
        is_published: Boolean(machine.is_published),
        secret_share_code: machine.secret_share_code ?? '',
        readme_text: machine.readme ?? '',
      });
    }
  }, [machine]);

  const invalidateMachine = () =>
    queryClient.invalidateQueries({ queryKey: getGetMachineQueryKey(publicId) });

  const update = useUpdateMachine({
    mutation: { onSuccess: () => navigate(`/m/${publicId}`) },
  });

  const upload = useMutation({
    mutationFn: (file: File) => uploadMachineImage(publicId, file),
    onSuccess: invalidateMachine,
  });
  const removeImage = useMutation({
    mutationFn: (imageId: string) => deleteMachineImage(publicId, imageId),
    onSuccess: invalidateMachine,
  });
  const setMain = useMutation({
    mutationFn: (imageId: string) => designateMainImage(publicId, imageId),
    onSuccess: invalidateMachine,
  });

  if (isLoading) return <p style={{ color: 'var(--text-secondary)' }}>Loading…</p>;
  if (error) return <p style={{ color: 'var(--danger)' }}>{errorMessage(error)}</p>;
  if (!machine) return <p>Machine not found.</p>;

  const isOwner = Boolean(user && machine.owner && machine.owner.username === user.username);
  if (!isOwner) {
    return (
      <div className="themed-card p-6 text-center">
        <p className="mb-3">You can only edit your own machines.</p>
        <Link to={`/m/${publicId}`} className="btn-primary">
          Back to machine
        </Link>
      </div>
    );
  }

  function save(): void {
    update.mutate({
      publicId,
      data: {
        name: form.name,
        short_description: form.short_description,
        is_published: form.is_published,
        secret_share_code: form.secret_share_code,
        tags: form.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        readme_text: form.readme_text,
      },
    });
  }

  return (
    <div className="flex flex-col gap-5 max-w-2xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Edit machine</h1>
        <Link to={`/m/${publicId}`} className="btn-secondary">
          Cancel
        </Link>
      </div>

      <label className="text-sm font-medium">Name</label>
      <input
        className="themed-input"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
      />

      <label className="text-sm font-medium">Short description</label>
      <input
        className="themed-input"
        value={form.short_description}
        onChange={(e) => setForm({ ...form, short_description: e.target.value })}
      />

      <label className="text-sm font-medium">Tags (comma-separated)</label>
      <input
        className="themed-input"
        value={form.tags}
        onChange={(e) => setForm({ ...form, tags: e.target.value })}
        placeholder="ubuntu, desktop, demo"
      />

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.is_published}
          onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
        />
        Published (visible on your profile and discoverable)
      </label>

      <label className="text-sm font-medium">README (Markdown)</label>
      <textarea
        className="themed-input font-mono text-sm"
        rows={8}
        value={form.readme_text}
        onChange={(e) => setForm({ ...form, readme_text: e.target.value })}
      />

      <label className="text-sm font-medium">Secret share code</label>
      <input
        className="themed-input"
        value={form.secret_share_code}
        onChange={(e) => setForm({ ...form, secret_share_code: e.target.value })}
      />

      <div className="flex items-center gap-3">
        <button className="btn-primary" onClick={save} disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save changes'}
        </button>
        {update.isError && (
          <span className="text-sm" style={{ color: 'var(--danger)' }}>
            {errorMessage(update.error)}
          </span>
        )}
      </div>

      {/* Image management */}
      <section className="flex flex-col gap-3 mt-2">
        <h2 className="text-lg font-semibold">Screenshots</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {(machine.images ?? []).map((img) => (
            <div key={img.public_id} className="themed-card p-2 flex flex-col gap-2">
              <img
                src={`/media/${img.image_path}`}
                alt={img.caption || machine.name}
                className="rounded w-full"
              />
              <div className="flex items-center justify-between text-xs">
                <span>{img.is_main ? '★ Main' : ''}</span>
                <div className="flex gap-2">
                  {!img.is_main && (
                    <button
                      className="btn-secondary text-xs"
                      onClick={() => setMain.mutate(img.id)}
                    >
                      Set main
                    </button>
                  )}
                  <button
                    className="btn-secondary text-xs"
                    style={{ color: 'var(--danger)' }}
                    onClick={() => removeImage.mutate(img.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
              if (fileInput.current) fileInput.current.value = '';
            }}
          />
          {upload.isPending && (
            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Uploading…
            </span>
          )}
          {upload.isError && (
            <span className="text-sm" style={{ color: 'var(--danger)' }}>
              {errorMessage(upload.error)}
            </span>
          )}
        </div>
      </section>
    </div>
  );
}
